.row {
	display: flex;
	gap: var(--spacing-3);
	align-items: center;
	min-block-size: 2.75rem;
	padding: var(--spacing-2) var(--spacing-3);
	color: inherit;
	text-decoration: none;
	cursor: pointer;
	background: var(--colour-background-surface);
	border: 1px solid var(--colour-border-subtle);
	border-radius: var(--radius-md);
	transition:
		border-color var(--motion-fast) var(--ease-standard),
		background-color var(--motion-fast) var(--ease-standard);

	&:hover {
		background: var(--colour-background-raised);
		border-color: var(--colour-border-strong);
	}

	/* Sortable rows (backlog) read as draggable. */
	&.draggable {
		cursor: grab;

		&:active {
			cursor: grabbing;
		}
	}

	/* Picked up: the source stays in its slot as a faded, dashed skeleton so you can
	   see where the row will drop — the lifted clone is the DragOverlay (.preview). */
	&.dragging {
		cursor: grabbing;
		border-color: var(--colour-border-strong);
		border-style: dashed;
		opacity: 0.4;
	}

	/* The floating clone under the cursor in the DragOverlay. */
	&.preview {
		z-index: 20;
		cursor: grabbing;
		background: var(--colour-background-raised);
		border-color: var(--colour-border-strong);
		box-shadow: var(--shadow-lg);
	}

	& .key {
		flex: none;
		inline-size: 4rem;
		font-family: var(--font-family-mono);
		font-size: var(--font-size-xs);
		color: var(--colour-text-secondary);
		letter-spacing: var(--letter-spacing-wide);
	}

	& .title {
		flex: 1 1 auto;
		min-inline-size: 0;
		overflow: hidden;
		text-overflow: ellipsis;
		font-size: var(--font-size-sm);
		font-weight: var(--font-weight-medium);
		color: var(--colour-text-primary);
		white-space: nowrap;
	}

	& .titleInput {
		flex: 1 1 auto;
		font-size: var(--font-size-sm);
	}

	& .labels {
		display: flex;
		flex: none;
		flex-wrap: wrap;
		gap: var(--spacing-1);
	}

	/* "⋯" trigger — hidden until row hover / focus / open, like the card. */
	& .menuButton {
		display: inline-flex;
		flex: none;
		align-items: center;
		justify-content: center;
		inline-size: 1.5rem;
		block-size: 1.5rem;
		padding: 0;
		font-size: var(--font-size-base);
		line-height: 1;
		color: var(--colour-text-secondary);
		cursor: pointer;
		background: transparent;
		border: none;
		border-radius: var(--radius-sm);
		opacity: 0;
		transition:
			opacity var(--motion-fast) var(--ease-standard),
			background-color var(--motion-fast) var(--ease-standard);

		&:hover {
			background: var(--colour-background-muted);
		}

		&:disabled {
			display: none;
		}
	}

	&:hover .menuButton,
	& .menuButton:focus-visible,
	& .menuButton[data-state='open'] {
		opacity: 1;
	}

	/* Between a drop and the next pointer move the card ignores the pointer, so its hover
	   state doesn't switch on under the cursor the instant the drag clone vanishes (TAS-69;
	   see hover-after-drop.ts). */
	:global(html[data-just-dropped]) & {
		pointer-events: none;
	}
}

/* Phones: the single-line row can't fit key + title + labels, and the title is
   what matters — narrow the key, drop the labels, and let the title wrap to two
   lines instead of truncating to a few words. (Labels stay one tap away in the
   editor.) The row stays centred (from the base rule) so the taller assignee
   avatar lines up with the key, title and badge instead of dropping below them. */
@media (width <= 640px) {
	.row {
		gap: var(--spacing-2);

		& .key {
			inline-size: 3rem;
		}

		/* Kept inline rather than using the shared `.line-clamp` utility: the
		   clamp only applies at this breakpoint (the base row truncates to a
		   single line), and a composed global class can't be breakpoint-gated. */
		& .title {
			display: -webkit-box;
			-webkit-line-clamp: 2;
			white-space: normal;
			-webkit-box-orient: vertical;
		}

		& .labels {
			display: none;
		}
	}
}
